import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import moment from "moment";
import * as React from "react";

import {DetailCell} from "@khanacademy/wonder-blocks-cell";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingXSmall,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";

import type {guideTranscripts_aiGuideThreads_threads as ThreadType} from "./guide-transcripts/__graphql-types__/guideTranscripts.ts";
import {ChatIcon} from "./icons/chat-icon.tsx";
import {ClockIcon} from "./icons/clock-icon.tsx";
import {DocumentIcon} from "./icons/document-icon.tsx";
import {
    countNumberOfInteractions,
    formatDateHeaderString,
    formatPageInfoString,
} from "./utils.ts";

import infoIcon from "@phosphor-icons/core/bold/info-bold.svg";

interface Props {
    threads: Array<ThreadType>;
    profilePath: string;
}

interface threadProps {
    thread: ThreadType;
    profilePath: string;
}

/**
 * A cell for an individual thread. The cell links to the particular
 * transcript for that thread and displays relevant info to the user, such
 * as time, conversation length, and content type. When a thread is flagged,
 * we tack on an informative label below the cell.
 */
const Thread = ({thread, profilePath}: threadProps): React.ReactElement => {
    const {id, lastUpdatedAt, flagged, title, coeditingDocument} = thread;
    return (
        <>
            <DetailCell
                title={
                    title ||
                    t`Khanmigo chat from ${moment(lastUpdatedAt).format("l LT")}`
                }
                testId="conversation"
                subtitle1={
                    <View style={styles.subtitle1}>
                        <ClockIcon />
                        <LabelSmall style={styles.iconLabel}>
                            {moment(lastUpdatedAt).format("LT")}
                        </LabelSmall>
                        <Strut size={spacing.medium_16} />
                        <ChatIcon />
                        <LabelSmall style={styles.iconLabel}>
                            {countNumberOfInteractions(thread)}
                        </LabelSmall>
                        {coeditingDocument && (
                            <>
                                <Strut size={spacing.medium_16} />
                                <DocumentIcon />
                                <LabelSmall style={styles.iconLabel}>
                                    {t`Document`}
                                </LabelSmall>
                            </>
                        )}
                    </View>
                }
                subtitle2={formatPageInfoString(thread)}
                href={`${profilePath}/khanmigo/history/${id}`}
                style={[styles.threadCell, flagged && styles.moderated]}
                horizontalRule="full-width"
            />
            {flagged && (
                <LabelSmall style={styles.moderatedLabel}>
                    <PhosphorIcon
                        icon={infoIcon}
                        size="small"
                        style={{
                            color: color.red,
                            alignSelf: "center",
                            marginInlineEnd: spacing.xxxSmall_4,
                        }}
                    />
                    {t`This conversation with Khanmigo has been flagged by Khan Academy's moderation system.`}
                </LabelSmall>
            )}
        </>
    );
};

/**
 * This component groups the individual threads by date and displays them
 * in a sectioned list.
 */
const Threads = ({threads, profilePath}: Props): React.ReactElement => {
    // Group the entire list of threads by date
    const dateGroups: Record<string, Array<ThreadType>> = threads.reduce(
        (dateGroups, thread) => {
            const date = moment(thread.lastUpdatedAt).format("l");
            if (!dateGroups[date]) {
                dateGroups[date] = [];
            }
            dateGroups[date].push(thread);
            return dateGroups;
        },
        {},
    );

    return (
        <>
            {Object.entries(dateGroups).map(([date, threadList], index) => (
                <View key={date}>
                    {index > 0 && <Strut size={spacing.large_24} />}
                    <HeadingXSmall style={styles.dateHeader}>
                        {formatDateHeaderString(date)}
                    </HeadingXSmall>
                    {threadList.map((thread) => (
                        <Thread
                            key={thread.id}
                            thread={thread}
                            profilePath={profilePath}
                        />
                    ))}
                </View>
            ))}
        </>
    );
};

const NoThreads = () => (
    <View>
        <Body>{t`No matching conversations were found.`}</Body>
    </View>
);

export default function ConversationsView({
    threads,
    profilePath,
}: Props): React.ReactElement {
    return (
        <View>
            {threads.length > 0 ? (
                <Threads threads={threads} profilePath={profilePath} />
            ) : (
                <NoThreads />
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    threadCell: {
        background: semanticColor.surface.secondary,
    },
    moderated: {
        background: semanticColor.feedback.critical.subtle.background,
    },
    moderatedLabel: {
        color: color.red,
        paddingInlineStart: spacing.xSmall_8,
        paddingBlockEnd: spacing.medium_16,
    },
    subtitle1: {
        flexDirection: "row",
        color: color.offBlack64,
    },
    dateHeader: {
        paddingInlineStart: spacing.medium_16,
        paddingBlockEnd: spacing.large_24,
    },
    iconLabel: {
        paddingInlineStart: spacing.xxxSmall_4,
    },
});
